import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LoggingService, ModelDisplay, ModelMetaData, PropertyTypeCode } from '@fgb/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';

/**
 * Helper service to create a form dynamically based on the model array
 */
@Injectable({
  providedIn: 'root'
})
export class ModelFormHelperService {
  private _baseData: any;
  private _form: UntypedFormGroup;
  today = new Date();
  models: ModelMetaData[];
  minAge = new NgbDate(this.today.getFullYear() - 18, this.today.getMonth() + 1, this.today.getDate());
  useShippingAddress: boolean | undefined = undefined;
  display = ModelDisplay;

  constructor(private _fb: UntypedFormBuilder, 
              private logService: LoggingService) { }


   /** Build form */
   buildForm(models: ModelMetaData[], data?: any): UntypedFormGroup{
    const type = Object.prototype.toString.call(models);

    if( type !== '[object Array]' ){
      this.logService.error(`Found ${typeof models}, expecting models to be array. Unable to create form.`, models);
      return this._form;
    }

    if( (type === '[object Array]' && models.filter(model => this._isInstaceOfModelMetaData(model)).length === 0) || !models){
      this.logService.error('No models found. Unable to create form.');
      return this._form;
    }
    
    this.models = models;
    this._form = this._buildFormFromModel(data || {});
    this._baseData = this.formData;
    
    return this._form;
  }

  /** Reset form value to base data */
  reset(){
    this._form.patchValue(this._baseData);
  }

  /** Dynamically create form based on the model array */
  private _buildFormFromModel(data?: any): UntypedFormGroup{
      const formData = new Map(this.models.filter(model => this._isInstaceOfModelMetaData(model))
        .map(e => [e['PropertyName'], this._createFormObjectFromModel(e, data[e['PropertyName']])]));

        if( this.useShippingAddress !== undefined ){ 
          // TODO: Leave ShouldUseShippingAddress for now, not sure what it does
          return this._fb.group({ ...Object.fromEntries(formData), ShouldUseShippingAddress: [ this.useShippingAddress ] })
        }

      return this._fb.group(Object.fromEntries(formData));
  }

  /** Checks if the object is instance of ModelMetaData */
  private _isInstaceOfModelMetaData(object: any): object is ModelMetaData{
      return 'PropertyName' in object;
  }

  /** Creates form object in preparation to create a form
  * @param model The model which the form will be dynamically created
  * @param value the value to be filled in the form created
  */
  private _createFormObjectFromModel(model: ModelMetaData, value?: any){
      let val = value || '';
      const validators = this._createValidatorsFromModel(model);
      const isDisabled = model.DisplayStatus === this.display.Hidden;

      if( model.PropertyName === 'DOB' || model.PropertyName === 'DateOfBirth' ){
          val = this._getNgbDateFromString(val);
      }
      
      return  [{ value: val, disabled: isDisabled }, validators ];
  }

  /** Create validators based on the model */
  private _createValidatorsFromModel(model: ModelMetaData): Validators[]{
    let validators: Validators[] = [];

    if( model.IsRequired ){
        validators = [ ...validators, Validators.required ];

        if( model.PropertyTypeCode === PropertyTypeCode.String || model.PropertyTypeCode === PropertyTypeCode.Region || model.PropertyTypeCode === PropertyTypeCode.PostCode){
          validators = [ ...validators, FGBValidators.Empty ];
        }
    }

    if( model.Validate ){
      validators = [ ...validators, Validators.pattern(model.ValidateExpression) ];
    }

    return validators;
  }

  /** Gets an NgbDate from a date string. */
  private _getNgbDateFromString(dateString: string): NgbDate | null {
    if (dateString) {
      const date = new Date(dateString);
      if (date) {
        return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      }
    }
    return null;
  }

  /** Gets a date string in ISO format from an NgbDate. */
  private _getStringFromNgbDate(ngbDate: NgbDate) {
    if (ngbDate) {
      let date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12);
      date.setUTCHours(0);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      return date.toISOString();
    } else {
      return '';
    }
  }

  get formData(){
    return this._form.value;
  }

  transformData(data: any){
    let cData = { ...data };
    if( cData.hasOwnProperty('DOB') ){
      cData = { ...cData, DOB: this._getStringFromNgbDate(cData['DOB']) };
    }
    if( cData.hasOwnProperty('DateOfBirth') ){
      cData = { ...cData, DateOfBirth: this._getStringFromNgbDate(cData['DateOfBirth']) };
    }

    return cData;
  }


}
